import React, { useState } from 'react';
import './App.css';

function compoundInterest(principal, rate, years) {
  let amount = principal * Math.pow((1 + rate / 100), years);
  return amount;
}

function calculateROI(initialCost, timeSpent, annualEarnings, years, debtAmount = 0.0, interestRate = 0.0, debtYears = null) {
  if (debtYears === null) {
    debtYears = years;
  }
  // Cap the debt repayment period at the examination window so we only count
  // interest that falls within the period being examined.
  let effectiveDebtYears = Math.min(Number(debtYears), Number(years));
  let totalEarnings = annualEarnings * (years - timeSpent);
  let debtCost = compoundInterest(debtAmount, interestRate, effectiveDebtYears);
  let totalCost = Number(initialCost) + debtCost - debtAmount;
  let netEarnings = totalEarnings - totalCost;
  let roi = (netEarnings / totalCost) * 100;
  return [roi, totalCost, netEarnings, debtCost, [initialCost, timeSpent, annualEarnings, years, debtAmount, interestRate, debtYears, totalEarnings]];
}

function ROICalculator({ title, setRoi }) {
  const [initialCost, setInitialCost] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [annualEarnings, setAnnualEarnings] = useState(0);
  const [years, setYears] = useState(0);
  const [debtAmount, setDebtAmount] = useState(0);
  const [interestRate, setInterestRate] = useState(0);
  const [debtYears, setDebtYears] = useState(0);
  const [output, setOutput] = useState([0, 0, 0, 0, [0, 0, 0, 0, 0, 0, 0, 0]]);

  const handleCalculate = (event) => {
    event.preventDefault();
    let result = calculateROI(initialCost, timeSpent, annualEarnings, years, debtAmount, interestRate, debtYears);
    setOutput(result);
    setRoi(result);
  };

  const debtExceedsWindow = Number(debtYears) > Number(years) && Number(debtAmount) > 0;

  return (
    <div className="ROICalculator">
      <h2>{title}</h2>
      <form onSubmit={handleCalculate}>
        <label>
          Your Program's Cost (Subtract Grants, but not Loans):
          <input type="number" value={initialCost} onChange={e => setInitialCost(e.target.value)} />
        </label>
        <label>
          Years In School (Time To Complete the Program):
          <input type="number" value={timeSpent} onChange={e => setTimeSpent(e.target.value)} />
        </label>
        <label>
          Expected Annual Earnings After Graduation (Averaged Over the Examination Period):
          <input type="number" value={annualEarnings} onChange={e => setAnnualEarnings(e.target.value)} />
        </label>
        <label>
          Total Examination Window (Years From Today, Including Time in School):
          <input type="number" value={years} onChange={e => setYears(e.target.value)} />
        </label>
        <label>
          Total Debt Amount (Principal):
          <input type="number" value={debtAmount} onChange={e => setDebtAmount(e.target.value)} />
        </label>
        <label>
          Annual Interest Rate (%):
          <input type="number" value={interestRate} onChange={e => setInterestRate(e.target.value)} />
        </label>
        <label>
          Anticipated Loan Repayment Period (Years):
          <input type="number" value={debtYears} onChange={e => setDebtYears(e.target.value)} />
        </label>
        <button type="submit">Calculate</button>
      </form>
      {debtExceedsWindow && (
        <p className="warning">
          ⚠️ Your loan repayment period ({Number(debtYears)} years) exceeds the examination window ({Number(years)} years). Interest has been calculated over {Number(years)} {Number(years) === 1 ? 'year' : 'years'} only.
        </p>
      )}
       <div className="output">
        <p><strong>ROI:</strong> {output[0].toFixed(2)}%</p>
        <p><strong>Total Cost:</strong> ${Number(output[1]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        <p><strong>Net Earnings (Total):</strong> ${Number(output[2]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        <p><strong>Net Earnings (Annual):</strong> ${years > 0 ? Number(output[2] / years).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : (0).toFixed(2)}</p>
        <p><strong>Cost of loans with interest:</strong> ${Number(output[3]).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
      </div>
    </div>
  );
}

function ROIComparison({ roi1, roi2 }) {
  let roiDiff = Math.abs(roi1[0] - roi2[0]);
  let totalCostDiff = Math.abs(roi1[1] - roi2[1]);
  let netEarningsDiff = Math.abs(roi1[2] - roi2[2]);
  let debtCostDiff = Math.abs(roi1[3] - roi2[3]);

  let earningsLeader = null;
  if (roi1[2] > roi2[2]) {
    earningsLeader = { program: 'Program Option 1', diff: roi1[2] - roi2[2] };
  } else if (roi2[2] > roi1[2]) {
    earningsLeader = { program: 'Program Option 2', diff: roi2[2] - roi1[2] };
  }

  return (
    <div className="ROIComparison">
      <h2>Comparison</h2>
      {earningsLeader && (
        <p className="earnings-leader">
          <strong>Higher Earnings Potential: {earningsLeader.program}</strong> — yields ${earningsLeader.diff.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} more in net earnings over the examined period.
        </p>
      )}
      <p><strong>ROI Difference:</strong> {roiDiff.toFixed(2)}%</p>
      <p><strong>Total Cost Difference:</strong> ${totalCostDiff.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
      <p><strong>Net Earnings Difference:</strong> ${netEarningsDiff.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
      <p><strong>Cost of Loans with Interest Difference:</strong> ${debtCostDiff.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
    </div>
  );
}

function App() {
  const [roi1, setRoi1] = useState([0, 0, 0, 0, [0, 0, 0, 0, 0, 0, 0, 0]]);
  const [roi2, setRoi2] = useState([0, 0, 0, 0, [0, 0, 0, 0, 0, 0, 0, 0]]);

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>Education Return on Investment Calculator</h1>
      </header>
      <div className="calculators-container">
        <ROICalculator title="Program Option 1" setRoi={setRoi1} />
        <ROICalculator title="Program Option 2" setRoi={setRoi2} />
      </div>
      <ROIComparison roi1={roi1} roi2={roi2} />
    </div>
  );
}

export default App;